/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type {
  AuthenticatePasskeyRequestPublicKey,
  AuthenticatorRegistrationOptionResponse,
} from '../managers/sso/models';

/** Apex hosts redirect AASA to www; iOS requires a valid webcredentials match on www. */
const APEX_TO_WWW_RP_ID: Record<string, string> = {
  'stgairasia.com': 'www.stgairasia.com',
  'airasia.com': 'www.airasia.com',
};

/**
 * SSO may return www/apex RP IDs; account-web must use the page origin so WebAuthn
 * accepts registration on account.stgairasia.com / account.airasia.com.
 */
const ACCOUNT_WEB_RP_IDS: Record<string, readonly string[]> = {
  'account.stgairasia.com': [
    'www.stgairasia.com',
    'stgairasia.com',
    'account.stgairasia.com',
  ],
  'account.airasia.com': [
    'www.airasia.com',
    'airasia.com',
    'account.airasia.com',
  ],
};

export type PasskeyRpIdContext = {
  /** When set (including `null`), skips reading `window.location.hostname`. */
  webHostname?: string | null;
};

const getWebOriginHostname = (): string | undefined => {
  try {
    if (typeof window === 'undefined' || !window.location?.hostname) {
      return undefined;
    }
    return window.location.hostname;
  } catch {
    return undefined;
  }
};

const resolveAccountWebRpId = (
  webHost: string,
  rpId: string,
): string | undefined => {
  const allowedServerRpIds = ACCOUNT_WEB_RP_IDS[webHost];
  if (allowedServerRpIds?.includes(rpId)) {
    return webHost;
  }
  return undefined;
};

export function normalizePasskeyRpId(
  rpId: string,
  context?: PasskeyRpIdContext,
): string {
  const webHost =
    context?.webHostname !== undefined
      ? (context.webHostname ?? undefined)
      : getWebOriginHostname();

  if (webHost) {
    const accountRpId = resolveAccountWebRpId(webHost, rpId);
    if (accountRpId) return accountRpId;
  }

  return APEX_TO_WWW_RP_ID[rpId] ?? rpId;
}

export function normalizePasskeyOptions(
  options: AuthenticatorRegistrationOptionResponse,
  context?: PasskeyRpIdContext,
): AuthenticatorRegistrationOptionResponse;
export function normalizePasskeyOptions(
  options: AuthenticatePasskeyRequestPublicKey,
  context?: PasskeyRpIdContext,
): AuthenticatePasskeyRequestPublicKey;
export function normalizePasskeyOptions(
  options:
    | AuthenticatorRegistrationOptionResponse
    | AuthenticatePasskeyRequestPublicKey,
  context?: PasskeyRpIdContext,
):
  | AuthenticatorRegistrationOptionResponse
  | AuthenticatePasskeyRequestPublicKey {
  const rpId =
    'rp' in options && options.rp?.id != null
      ? options.rp.id
      : 'rpId' in options
        ? options.rpId
        : undefined;
  if (rpId == null) return options;

  const normalized = normalizePasskeyRpId(rpId, context);
  if (normalized === rpId) return options;

  if ('rp' in options && options.rp) {
    return { ...options, rp: { ...options.rp, id: normalized } };
  }
  return { ...options, rpId: normalized };
}
